import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Megaphone, Phone, MapPin, Send, Loader2, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { friendlyError } from "@/lib/api";
import { postsApi } from "@/lib/endpoints";
import { PhoneInput } from "@/components/PhoneInput";
import { LocationSelect } from "@/components/LocationSelect";
import { ReportAbuseButton } from "@/components/ModerationActions";

const MAX = 500;

const timeAgoFactory = (t) => (iso) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return t("wall.justNow");
  if (m < 60) return t("wall.minAgo", { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("wall.hAgo", { n: h });
  return t("wall.dAgo", { n: Math.floor(h / 24) });
};

const labelCls = "block text-sm font-semibold text-neutral-700 mb-1";
const inputCls = "w-full rounded-sm border border-neutral-300 bg-white px-3 min-h-[48px] text-base outline-none focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC]";

function PostCard({ p, onReport, t, timeAgo }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm p-3 flex flex-col gap-2" data-testid={`post-card-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-neutral-900 truncate">{p.name || t("wall.anon")}</span>
        <span className="text-xs text-neutral-400 shrink-0">{timeAgo(p.created_at)}</span>
      </div>
      <p className="text-neutral-800 whitespace-pre-wrap break-words">{p.message}</p>
      {(p.state || p.municipality) && (
        <span className="text-xs text-neutral-500 uppercase tracking-wide flex items-center gap-1">
          <MapPin className="w-3 h-3 shrink-0" /> {[p.municipality, p.state].filter(Boolean).join(", ")}
        </span>
      )}
      <a href={`tel:${p.contact_phone}`} data-testid={`post-contact-${p.id}`}
        className="inline-flex items-center gap-2 bg-[#166534] text-white hover:opacity-90 rounded-sm min-h-[44px] px-3 font-semibold w-full justify-center">
        <Phone className="w-4 h-4" /> {t("wall.contact", { phone: p.contact_phone })}
      </a>
      <div className="pt-1">
        <ReportAbuseButton testid={`post-report-${p.id}`} onReport={(reason) => onReport(p.id, reason)} />
      </div>
    </div>
  );
}

export default function HelpWall() {
  const { t } = useTranslation();
  const timeAgo = timeAgoFactory(t);
  const [posts, setPosts] = useState(null);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [loc, setLoc] = useState({ state: "", municipality: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetchPosts = useCallback(() => {
    postsApi.list().then(setPosts).catch(() => setPosts((p) => p ?? []));
  }, []);

  useEffect(() => {
    fetchPosts();
    const timer = setInterval(fetchPosts, 30 * 1000); // auto-actualización "en vivo"
    return () => clearInterval(timer);
  }, [fetchPosts]);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error(t("wall.toastWriteMessage")); return; }
    if (message.length > MAX) { toast.error(t("wall.toastMax", { max: MAX })); return; }
    if (phone.replace(/\D/g, "").length < 9) { toast.error(t("wall.toastValidPhone")); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("message", message.trim());
      fd.append("contact_phone", phone);
      fd.append("name", name.trim());
      fd.append("state", loc.state);
      fd.append("municipality", loc.municipality);
      await postsApi.create(fd);
      toast.success(t("wall.toastPublished"));
      setName(""); setMessage(""); setPhone(""); setLoc({ state: "", municipality: "" });
      fetchPosts();
    } catch (err) {
      toast.error(friendlyError(err, t("wall.toastError")));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="help-wall-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-neutral-900 flex items-center gap-2">
          <Megaphone className="w-6 h-6 text-[#0066CC]" /> {t("wall.title")}
        </h1>
        <p className="text-sm text-neutral-600 mt-1">
          {t("wall.subtitleA")}<b>{t("wall.needSomething")}</b>{t("wall.subtitleB")}<b>{t("wall.canHelp")}</b>{t("wall.subtitleC")}
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-3" data-testid="post-form">
        <div>
          <label className={labelCls}>{t("wall.yourName")}</label>
          <input className={inputCls} data-testid="post-name" value={name} maxLength={80}
            onChange={(e) => setName(e.target.value)} placeholder={t("wall.yourNamePlaceholder")} />
        </div>
        <div>
          <label className={labelCls}>{t("wall.message")}</label>
          <textarea data-testid="post-message" value={message} rows={4} maxLength={MAX}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("wall.messagePlaceholder")}
            className="w-full rounded-sm border border-neutral-300 bg-white px-3 py-2 text-base outline-none focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC] resize-none" />
          <span className="text-xs text-neutral-400">{message.length}/{MAX}</span>
        </div>
        <div>
          <label className={labelCls}>{t("wall.contactPhone")}</label>
          <PhoneInput testid="post-phone" value={phone} onChange={setPhone} placeholder={t("phone.numberPlaceholder")} />
        </div>
        <div>
          <label className={labelCls}>{t("wall.location")}</label>
          <LocationSelect state={loc.state} municipality={loc.municipality}
            onChange={setLoc} prefix="post-loc" />
        </div>
        <button type="submit" disabled={submitting} data-testid="post-submit"
          className="bg-[#0066CC] text-white hover:bg-[#0052A3] disabled:opacity-60 rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2">
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("wall.publishing")}</> : <><Send className="w-4 h-4" /> {t("wall.publish")}</>}
        </button>
      </form>

      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-neutral-800">{t("wall.recent")}</h2>
        <button onClick={fetchPosts} data-testid="post-refresh"
          className="text-xs text-neutral-500 hover:text-[#0066CC] flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> {t("wall.refresh")}
        </button>
      </div>

      {posts === null ? (
        <p className="text-sm text-neutral-500 text-center py-8">{t("wall.loadingPosts")}</p>
      ) : posts.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-8" data-testid="post-empty">
          {t("wall.empty")}
        </p>
      ) : (
        <div className="flex flex-col gap-3" data-testid="posts-list">
          {posts.map((p) => <PostCard key={p.id} p={p} onReport={postsApi.reportAbuse} t={t} timeAgo={timeAgo} />)}
        </div>
      )}
    </div>
  );
}
